import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  formatYmdLocal,
  resolveHistoryRange,
  todayYmd,
} from '../utils/trainingHistoryRange';
import {
  previousSetHint,
  formatPrevWeekLabel,
  logHasComparableLoad,
  mergeIsoWeekHistory,
} from '../utils/trainingPrevWeekHint';
import api from '../services/api';
import { useAuth } from '../context/useAuth';
import { isConsumerAccessBlocked } from '../utils/subscriptionAccess';
import TrainingBodyMeasurements from './training/TrainingBodyMeasurements';
import TrainingPendingBanner from './training/TrainingPendingBanner';
import TrainingFoodSyncBanner from './training/TrainingFoodSyncBanner';
import GalleryVideoThumbnail from './training/GalleryVideoThumbnail';
import { videoUrlToEmbed } from '../utils/videoEmbed';
import SportAthleteLogForm from './training/SportAthleteLogForm';
import {
  emptySportLog,
  hydrateSportLogFromApi,
  isSportSessionKind,
  normalizeSessionKind,
  resolveDaySessionKind,
  serializeSportLog,
  sportLogHasAthleteData,
  SESSION_KINDS,
} from '../utils/trainingSportSession';
import { formatDayWithWeekday, weekdayLabel } from '../utils/trainingWeekday';
import { athleteFirstName, coachSpokenName, summarizeAssignedWeek } from '../utils/athleteCoachWelcome';
import AthleteCoachWelcome from './training/AthleteCoachWelcome';

function toEmbedUrl(url) {
  return videoUrlToEmbed(url);
}

/** ¿El ejercicio del diario tiene datos reales del atleta? */
function exerciseLogHasAthleteData(ex) {
  if (!ex) return false;
  if (String(ex.notes || '').trim()) return true;
  if (ex.reps) return true;
  if (ex.sets) return true;
  if (ex.weight) return true;
  if (ex.intensity) return true;
  if (ex.setReps && Object.values(ex.setReps).some(Boolean)) return true;
  if (ex.setWeights && Object.values(ex.setWeights).some(Boolean)) return true;
  if (ex.setIntensities && Object.values(ex.setIntensities).some(Boolean)) return true;
  return false;
}

function workoutLogHasAthleteData(logNow) {
  if (!logNow) return false;
  if (sportLogHasAthleteData(logNow.sportLog)) return true;
  // Cardio del plan (placeholder) NO cuenta como dato del atleta; solo series/notas.
  const exercises = logNow.exercises;
  if (!exercises) return false;
  const list = Array.isArray(exercises) ? exercises : Object.values(exercises);
  return list.some(exerciseLogHasAthleteData);
}

/** El plan del coach no trae completado por sesión; solo lo marca el atleta en el diario. */
function stripPlanCompletado(planData) {
  if (!planData?.dias?.length) return planData;
  return {
    ...planData,
    dias: planData.dias.map((d) => ({ ...d, completado: false })),
  };
}

/** Solo mostrar 2/3 marcado si el atleta lo confirmó (ack/sesión real), no bleed legacy. */
function shouldTrustCompletadoFromLog(log) {
  if (log?.completado !== true) return false;
  if (logHasComparableLoad(log)) return true;
  return false;
}

/** Log persistido vacío (autosave / cronómetro sin series) — no mostrar en historial. */
function isEmptyTrainingHistoryStub(log) {
  if (!log) return true;
  if (log.session_started_at && !log.session_ended_at) return false;
  return !logHasComparableLoad(log);
}

function trainingHistoryStatusLabel(log) {
  if (shouldTrustCompletadoFromLog(log)) return 'Completado';
  if (log?.session_started_at && !log?.session_ended_at) return 'En progreso';
  if (!isEmptyTrainingHistoryStub(log)) return 'Registrado';
  return null;
}

/** Valores programados por el coach (campos planos del plan) antes que prescription/auto. */
function exercisePrescriptionLabel(ex) {
  const sets = Number(ex?.sets ?? ex?.prescription?.sets);
  const setsLabel = Number.isFinite(sets) && sets > 0 ? sets : null;
  const repsRaw = ex?.reps ?? ex?.prescription?.reps;
  const repsLabel = repsRaw != null && String(repsRaw).trim() !== ''
    ? String(repsRaw).trim()
    : null;
  const intensityType = String(ex?.intensity_type || ex?.prescription?.intensity_type || '').trim().toUpperCase();
  const intensityVal = ex?.intensity_value ?? ex?.prescription?.intensity_value ?? ex?.prescription?.target_rpe;
  const hasIntensity = intensityVal != null && String(intensityVal).trim() !== '';
  const intensityLabel = hasIntensity
    ? `${intensityType === 'RIR' || intensityType === 'RPE' ? intensityType : (intensityType || 'RIR')} ${String(intensityVal).trim()}`
    : null;
  const rest = Number(ex?.rest_seconds);
  const restLabel = Number.isFinite(rest) && rest > 0 ? `Descanso ${rest}s` : null;

  const parts = [];
  if (setsLabel != null && repsLabel) {
    parts.push(`${setsLabel} series x ${repsLabel} repeticiones`);
  } else if (setsLabel != null) {
    parts.push(`${setsLabel} series`);
  } else if (repsLabel) {
    parts.push(`${repsLabel} repeticiones`);
  }
  if (intensityLabel) parts.push(intensityLabel);
  if (restLabel) parts.push(restLabel);
  return parts.length ? parts.join(' | ') : 'Sin prescripción programada';
}

function exerciseSetCount(ex) {
  const n = Number(ex?.sets ?? ex?.prescription?.sets);
  return Number.isFinite(n) && n > 0 ? n : 3;
}

function PrevWeekSetHint({ hint }) {
  if (!hint) return null;
  const bits = [
    hint.reps ? `${hint.reps} reps` : null,
    hint.kg ? `${hint.kg} kg` : null,
    hint.rir ? `RIR ${hint.rir}` : null,
  ].filter(Boolean);
  if (!bits.length) return null;
  return (
    <p className="text-[10px] text-amber-900 bg-amber-50 border border-amber-100 rounded px-1.5 py-0.5 mt-1 mb-0 leading-snug">
      {formatPrevWeekLabel(hint.fecha)}: {bits.join(' · ')}
    </p>
  );
}

export default function TrainingModule({ initialSubView = 'workout' }) {
  const { user } = useAuth();
  const coachWl = Boolean(user?.coach_branding?.white_label_enabled);
  const coachAssigned = Boolean(user?.trainer_id);

  const [level, setLevel] = useState('principiante');
  const [method, setMethod] = useState('hipertrofia');
  const [daysAvailable, setDaysAvailable] = useState(4);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [plan, setPlan] = useState(null);
  const [selectedDayIndex, setSelectedDayIndex] = useState(0);
  const [selectedExerciseIndex, setSelectedExerciseIndex] = useState(0);
  /** Fuerza remount del iframe YouTube al cambiar de ejercicio (ida y vuelta). */
  const [videoMountKey, setVideoMountKey] = useState(0);
  const [historyMode, setHistoryMode] = useState('week');
  const [weekOffset, setWeekOffset] = useState(0);
  const [rangeFrom, setRangeFrom] = useState('');
  const [rangeTo, setRangeTo] = useState('');
  const [historyLogs, setHistoryLogs] = useState([]);
  const [historyFecha, setHistoryFecha] = useState(() => todayYmd());
  const [previousLog, setPreviousLog] = useState(null);

  const historyRange = useMemo(
    () => resolveHistoryRange(historyMode, { weekOffset, rangeFrom, rangeTo }),
    [historyMode, weekOffset, rangeFrom, rangeTo],
  );

  const isViewingPast = historyFecha && historyFecha !== todayYmd();

  const selectExercise = (index) => {
    const next = Number(index) || 0;
    setSelectedExerciseIndex((prev) => {
      if (prev === next) return prev;
      setVideoMountKey((k) => k + 1);
      return next;
    });
  };

  // Entrenador asignado (si el usuario tiene trainer_id).
  const [trainer, setTrainer] = useState(null);
  const [showTrainer, setShowTrainer] = useState(false);

  const [assistantExercise, setAssistantExercise] = useState(null);
  const [assistantReason, setAssistantReason] = useState('');
  const [assistantLoading, setAssistantLoading] = useState(false);
  const [assistantResult, setAssistantResult] = useState(null);
  const [substituteAlternatives, setSubstituteAlternatives] = useState([]);

  const CAUSE_PRESETS = [
    'Dolor de hombro',
    'Dolor de rodilla',
    'Equipo no disponible',
    'Fatiga articular',
    'Poco tiempo',
  ];

  // === WORKOUT LOGGING STATE ===
  const [workoutLog, setWorkoutLog] = useState({ 
    cardioBpm: '', 
    cardioLimit: '', 
    exercises: {},
    sportLog: emptySportLog(SESSION_KINDS.RUNNING),
    wellness: {
      sleep_hours: 8,
      sleep_quality: 5,
      stress_level: 5,
      appetite: 5,
      energy_level: 5
    }
  });
  const [savingLog, setSavingLog] = useState(false);
  const [sessionActive, setSessionActive] = useState(false);
  const [sessionStartedAt, setSessionStartedAt] = useState(null);
  const [sessionElapsedSec, setSessionElapsedSec] = useState(0);
  const [galleryMeta, setGalleryMeta] = useState(null);

  const planRef = useRef(null);
  const dayRef = useRef(null);
  const workoutLogRef = useRef(workoutLog);
  const sessionActiveRef = useRef(sessionActive);
  const sessionStartedAtRef = useRef(sessionStartedAt);
  const sessionElapsedSecRef = useRef(sessionElapsedSec);
  const historyFechaRef = useRef(historyFecha);
  const diaryHydratedRef = useRef(false);
  const persistInFlightRef = useRef(null);
  const autosaveTimerRef = useRef(null);

  const accessBlocked = isConsumerAccessBlocked(user);

  const [galleryByName, setGalleryByName] = useState({});
  const [galleryItems, setGalleryItems] = useState([]);
  const [subView, setSubView] = useState(initialSubView);
  const [galleryCoachFilter, setGalleryCoachFilter] = useState('');
  const [galleryCategoryFilter, setGalleryCategoryFilter] = useState('');
  const [gallerySearch, setGallerySearch] = useState('');
  const [galleryModalItem, setGalleryModalItem] = useState(null);

  const buildWorkoutLogFromSaved = (log, dayRow) => {
    const exercises = {};
    (log?.ejercicios || []).forEach((ex, i) => {
      exercises[i] = {
        reps: ex.reps_done || '',
        sets: ex.sets_done || '',
        weight: ex.weight_kg || '',
        intensity: ex.intensity_actual || '',
        setReps: ex.set_reps || {},
        setWeights: ex.set_weights || {},
        setIntensities: ex.set_intensities || {},
        notes: ex.notes || '',
      };
    });
    const kind = isSportSessionKind(normalizeSessionKind(log?.session_kind))
      ? normalizeSessionKind(log.session_kind)
      : resolveDaySessionKind(dayRow || {});
    return {
      // Solo cardio ya guardado por el atleta; el del plan va en placeholder del input.
      cardioBpm: log?.cardio_bpm != null ? String(log.cardio_bpm) : '',
      cardioLimit: log?.cardio_limit != null ? String(log.cardio_limit) : '',
      exercises,
      sportLog: hydrateSportLogFromApi(kind, log?.sport_log)
        || emptySportLog(kind === SESSION_KINDS.GYM ? SESSION_KINDS.RUNNING : kind),
      wellness: {
        sleep_hours: 8,
        sleep_quality: 5,
        stress_level: 5,
        appetite: 5,
        energy_level: 5,
        ...(log?.wellness || {}),
      },
    };
  };

  const applySessionFromLog = (log) => {
    if (!log) {
      setSessionActive(false);
      setSessionStartedAt(null);
      setSessionElapsedSec(0);
      return;
    }
    if (log.session_started_at && !log.session_ended_at) {
      const started = new Date(log.session_started_at).getTime();
      if (!Number.isNaN(started)) {
        setSessionActive(true);
        setSessionStartedAt(started);
        setSessionElapsedSec(Math.max(0, Math.floor((Date.now() - started) / 1000)));
        return;
      }
    }
    setSessionActive(false);
    setSessionStartedAt(null);
    if (log.duration_minutes) {
      setSessionElapsedSec(Math.max(0, Number(log.duration_minutes) * 60));
    } else {
      setSessionElapsedSec(0);
    }
  };

  useEffect(() => {
    setSubView(initialSubView);
  }, [initialSubView]);

  useEffect(() => {
    const fetchCurrentPlan = async () => {
      try {
        const resp = await api.get('/training/my-current-plan');
        if (resp.data?.success && resp.data?.plan?.dias?.length) {
          setPlan(stripPlanCompletado(resp.data.plan));
        }
      } catch {
        // silent
      }
    };
    fetchCurrentPlan();
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await api.get('/training/gallery');
        const items = res.data?.data || [];
        const map = {};
        for (const item of items) {
          const key = String(item.name || '').trim().toLowerCase();
          if (key) map[key] = item;
        }
        if (active) {
          setGalleryByName(map);
          setGalleryItems(items);
          setGalleryMeta(res.data?.meta || null);
        }
      } catch {
        if (active) setGalleryByName({});
      }
    })();
    return () => { active = false; };
  }, []);

  // Carga del entrenador asignado para el botón "Ver entrenador".
  useEffect(() => {
    const trainerId = user?.trainer_id;
    if (!trainerId) { setTrainer(null); return; }
    let active = true;
    (async () => {
      try {
        const r = await api.get(`/trainers/${trainerId}`);
        if (active) setTrainer(r.data?.data || r.data || null);
      } catch {
        if (active) setTrainer(null);
      }
    })();
    return () => { active = false; };
  }, [user?.trainer_id]);

  const generateDailyPlan = async () => {
    try {
      setLoading(true);
      setError('');
      const resp = await api.post('/training/generate-daily-plan', {
        level,
        method,
        days_available: Number(daysAvailable),
      });
      if (resp.data?.success) {
        setPlan(stripPlanCompletado(resp.data.data));
        setSelectedDayIndex(0);
        setSelectedExerciseIndex(0);
      } else {
        setError('Error al generar el plan.');
      }
    } catch (e) {
      console.error(e);
      setError('No pudimos generar el plan en este momento. Intenta nuevamente o avisa a tu coach.');
    } finally {
      setLoading(false);
    }
  };

  const day = useMemo(() => {
    const days = plan?.dias || [];
    return days[selectedDayIndex] || null;
  }, [plan, selectedDayIndex]);
  const daySessionKind = useMemo(
    () => resolveDaySessionKind(day || {}),
    [day],
  );
  const dayIsSport = isSportSessionKind(daySessionKind);
  const spokenCoach = coachSpokenName({ trainer, branding: user?.coach_branding });
  const spokenAthlete = athleteFirstName(user);
  const weekDays = useMemo(() => summarizeAssignedWeek(plan?.dias), [plan?.dias]);

  useEffect(() => { planRef.current = plan; }, [plan]);
  useEffect(() => { dayRef.current = day; }, [day]);
  useEffect(() => { workoutLogRef.current = workoutLog; }, [workoutLog]);
  useEffect(() => { sessionActiveRef.current = sessionActive; }, [sessionActive]);
  useEffect(() => { sessionStartedAtRef.current = sessionStartedAt; }, [sessionStartedAt]);
  useEffect(() => { sessionElapsedSecRef.current = sessionElapsedSec; }, [sessionElapsedSec]);
  useEffect(() => { historyFechaRef.current = historyFecha; }, [historyFecha]);

  const diaryDraftKey = useCallback((planId, dia, fecha) => (
    `training.diary.draft.${planId}.${dia}.${fecha || todayYmd()}`
  ), []);

  const writeDiaryDraft = useCallback((planId, dia, fecha, payload) => {
    if (!planId || dia == null) return;
    try {
      localStorage.setItem(diaryDraftKey(planId, dia, fecha), JSON.stringify({
        ...payload,
        savedAt: Date.now(),
      }));
    } catch { /* quota / private mode */ }
  }, [diaryDraftKey]);

  const readDiaryDraft = useCallback((planId, dia, fecha) => {
    try {
      const raw = localStorage.getItem(diaryDraftKey(planId, dia, fecha));
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }, [diaryDraftKey]);

  const clearDiaryDraft = useCallback((planId, dia, fecha) => {
    try {
      localStorage.removeItem(diaryDraftKey(planId, dia, fecha));
    } catch { /* ignore */ }
  }, [diaryDraftKey]);

  const loadHistoryLogs = useCallback(async () => {
    try {
      const params = {};
      if (historyMode === 'week' && historyRange.from) {
        const lookback = new Date(`${historyRange.from}T12:00:00`);
        lookback.setDate(lookback.getDate() - 42);
        params.from = formatYmdLocal(lookback);
        if (historyRange.to) params.to = historyRange.to;
      } else {
        if (historyRange.from) params.from = historyRange.from;
        if (historyRange.to) params.to = historyRange.to;
      }
      const res = await api.get('/training/my-logs', { params });
      const rows = Array.isArray(res.data?.data) ? res.data.data : [];
      const real = rows.filter((log) => !isEmptyTrainingHistoryStub(log));
      const next = historyMode === 'week' && historyRange.from && historyRange.to
        ? mergeIsoWeekHistory(real, historyRange.from, historyRange.to)
        : real;
      setHistoryLogs(next);
    } catch {
      setHistoryLogs([]);
    }
  }, [historyMode, historyRange.from, historyRange.to]);

  const persistDiary = useCallback(async (opts = {}) => {
    const options = typeof opts === 'boolean'
      ? { fromSessionFinish: opts === true, silent: false }
      : (opts || {});
    const {
      silent = true,
      fromSessionFinish = false,
      override = {},
      completadoAck = false,
    } = options;

    const planNow = override.plan ?? planRef.current;
    const dayNow = override.day ?? dayRef.current;
    const logNow = override.workoutLog ?? workoutLogRef.current;
    const startedAt = override.sessionStartedAt !== undefined
      ? override.sessionStartedAt
      : sessionStartedAtRef.current;
    const elapsed = override.sessionElapsedSec !== undefined
      ? override.sessionElapsedSec
      : sessionElapsedSecRef.current;
    const fecha = override.historyFecha
      ?? historyFechaRef.current
      ?? todayYmd();

    if (!planNow?.id || !dayNow?.dia) return false;

    writeDiaryDraft(planNow.id, dayNow.dia, fecha, {
      workoutLog: logNow,
      sessionActive: override.sessionActive !== undefined
        ? override.sessionActive
        : sessionActiveRef.current,
      sessionStartedAt: startedAt,
      sessionElapsedSec: elapsed,
    });

    const finishing = fromSessionFinish === true;
    const forceServer = !silent || finishing || options.force === true;
    const sessionLive = Boolean(startedAt);
    const meaningful = forceServer
      || sessionLive
      || (Boolean(dayNow.completado) && completadoAck)
      || workoutLogHasAthleteData(logNow);

    // No crear filas fantasma en historial solo por entrar / cambiar de día / flush.
    if (!meaningful) return false;
    // Guardar vacío (sin sesión ni series): no llamar API ni fingir éxito.
    const kindNow = resolveDaySessionKind(dayNow);
    const sportOk = isSportSessionKind(kindNow) && sportLogHasAthleteData(logNow.sportLog);
    if (forceServer && !sessionLive && !dayNow.completado && !workoutLogHasAthleteData(logNow) && !finishing && !sportOk) {
      if (!silent) {
        alert('Aún no hay nada que guardar. Escribe tus series, km o tiempo y vuelve a pulsar guardar.');
      }
      return false;
    }

    const run = async () => {
      try {
        if (!silent) setSavingLog(true);
        const sessionEndedAt = finishing && startedAt ? new Date().toISOString() : null;
        const sessionStartedIso = startedAt ? new Date(startedAt).toISOString() : null;
        const sessionKind = resolveDaySessionKind(dayNow);
        const sportPayload = isSportSessionKind(sessionKind)
          ? serializeSportLog(sessionKind, logNow.sportLog)
          : null;
        const payload = {
          plan_id: planNow.id,
          dia: dayNow.dia,
          fecha,
          session_started_at: sessionStartedIso,
          session_ended_at: sessionEndedAt,
          session_kind: sessionKind,
          sport_log: sportPayload,
          cardio_bpm: logNow.cardioBpm,
          cardio_limit: logNow.cardioLimit,
          ejercicios: (dayNow.ejercicios || []).map((ex, i) => {
            const logData = logNow.exercises?.[i] || {};
            const setReps = logData.setReps || {};
            const setWeights = logData.setWeights || {};
            const setIntensities = logData.setIntensities || {};
            const repsFromSets = Object.values(setReps).filter(Boolean).join(' / ');
            const weightsFromSets = Object.values(setWeights).filter(Boolean).join(' / ');
            const intensitiesFromSets = Object.values(setIntensities).filter(Boolean).join(' / ');
            const filledSetIndexes = new Set([
              ...Object.keys(setReps).filter((k) => setReps[k]),
              ...Object.keys(setWeights).filter((k) => setWeights[k]),
              ...Object.keys(setIntensities).filter((k) => setIntensities[k]),
            ]);
            const firstWeight = Object.values(setWeights).find((w) => w !== '' && w != null);
            return {
              exercise_name: ex.exercise_name,
              sets_done: filledSetIndexes.size || (logData.sets ? Number(logData.sets) : 0),
              reps_done: repsFromSets || logData.reps || '',
              set_reps: setReps,
              set_weights: setWeights,
              set_intensities: setIntensities,
              weight_kg: firstWeight ? Number(firstWeight) : (logData.weight ? Number(logData.weight) : 0),
              weights_done: weightsFromSets,
              intensity_actual: intensitiesFromSets || logData.intensity || '',
              notes: String(logData.notes || '').trim(),
            };
          }),
          wellness: logNow.wellness,
        };
        if (completadoAck || !silent) {
          payload.completado = Boolean(dayNow.completado);
          payload.completado_ack = completadoAck === true;
        }
        // Deportes: duración digitada. Gimnasio: timer de sesión.
        if (isSportSessionKind(sessionKind) && sportPayload?.duration_sec > 0) {
          payload.duration_minutes = Math.max(1, Math.round(sportPayload.duration_sec / 60));
        } else if (elapsed > 0) {
          payload.duration_minutes = Math.max(1, Math.round(elapsed / 60));
        } else if (finishing) {
          payload.duration_minutes = 0;
        }
        const resp = await api.post('/training/log', payload);
        if (resp.data?.success && resp.data?.skipped) {
          // Backend no creó fila (vacío): no fingir guardado ni borrar draft.
          if (!silent) {
            alert('Aún no hay nada que guardar. Escribe tus series, km o tiempo y vuelve a pulsar guardar.');
          }
          return false;
        }
        if (resp.data?.success && resp.data?.data) {
          clearDiaryDraft(planNow.id, dayNow.dia, fecha);
          if (!silent || finishing) {
            applySessionFromLog(resp.data.data);
          }
          loadHistoryLogs();
          if (!silent) {
            if (finishing) {
              const h = Math.floor(elapsed / 3600);
              const m = Math.floor((elapsed % 3600) / 60);
              const s = elapsed % 60;
              const label = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
              alert(`Sesión finalizada (${label})`);
            } else {
              alert('Entrenamiento guardado correctamente');
            }
          }
          return true;
        }
        return false;
      } catch (e) {
        console.error(e);
        if (!silent) alert('Error al guardar el entrenamiento');
        return false;
      } finally {
        if (!silent) setSavingLog(false);
      }
    };

    if (persistInFlightRef.current) {
      try { await persistInFlightRef.current; } catch { /* continue */ }
    }
    const pending = run();
    persistInFlightRef.current = pending;
    try {
      return await pending;
    } finally {
      if (persistInFlightRef.current === pending) persistInFlightRef.current = null;
    }
  }, [clearDiaryDraft, loadHistoryLogs, writeDiaryDraft]);

  useEffect(() => {
    if (!plan?.id || !day?.dia) return undefined;
    let active = true;
    diaryHydratedRef.current = false;
    (async () => {
      try {
        const params = { plan_id: plan.id, dia: day.dia };
        if (historyFecha) params.fecha = historyFecha;
        const res = await api.get('/training/log/day', { params });
        if (!active) return;
        const log = res.data?.data;
        const fecha = historyFecha || todayYmd();
        setPreviousLog(res.data?.previous || null);
        const draft = readDiaryDraft(plan.id, day.dia, fecha);
        const hasServerData = Boolean(
          log
          && (
            (Array.isArray(log.ejercicios) && log.ejercicios.length > 0)
            || log.session_started_at
            || log.cardio_bpm
            || log.wellness
          ),
        );
        if (!hasServerData && draft?.workoutLog) {
          setWorkoutLog(draft.workoutLog);
          if (draft.sessionStartedAt && draft.sessionActive && !draft.sessionEndedAt) {
            setSessionActive(true);
            setSessionStartedAt(draft.sessionStartedAt);
            setSessionElapsedSec(
              Math.max(0, Math.floor((Date.now() - Number(draft.sessionStartedAt)) / 1000)),
            );
          } else {
            applySessionFromLog(null);
          }
        } else {
          setWorkoutLog(buildWorkoutLogFromSaved(log, day));
          applySessionFromLog(log);
        }
        const completadoForSession = shouldTrustCompletadoFromLog(log);
        setPlan((prev) => {
          if (!prev?.dias?.length) return prev;
          return {
            ...prev,
            dias: prev.dias.map((d, i) => (
              i === selectedDayIndex ? { ...d, completado: completadoForSession } : d
            )),
          };
        });
      } catch {
        if (!active) return;
        setPreviousLog(null);
        const draft = readDiaryDraft(plan.id, day.dia, historyFecha || todayYmd());
        if (draft?.workoutLog) {
          setWorkoutLog(draft.workoutLog);
          if (draft.sessionStartedAt && draft.sessionActive) {
            setSessionActive(true);
            setSessionStartedAt(draft.sessionStartedAt);
            setSessionElapsedSec(
              Math.max(0, Math.floor((Date.now() - Number(draft.sessionStartedAt)) / 1000)),
            );
          } else {
            applySessionFromLog(null);
          }
        } else {
          setWorkoutLog(buildWorkoutLogFromSaved(null, day));
          applySessionFromLog(null);
        }
        setPlan((prev) => {
          if (!prev?.dias?.length) return prev;
          return {
            ...prev,
            dias: prev.dias.map((d, i) => (
              i === selectedDayIndex ? { ...d, completado: false } : d
            )),
          };
        });
      } finally {
        if (active) diaryHydratedRef.current = true;
      }
    })();
    return () => { active = false; };
  }, [plan?.id, day?.dia, selectedDayIndex, historyFecha, readDiaryDraft]);

  useEffect(() => {
    if (!plan?.id) return undefined;
    loadHistoryLogs();
    return undefined;
  }, [plan?.id, loadHistoryLogs]);

  useEffect(() => {
    if (historyMode !== 'week' || !historyRange.from || !historyRange.to) return;
    const f = String(historyFecha || '').slice(0, 10);
    if (f && f >= historyRange.from && f <= historyRange.to) return;
    const inWeek = historyLogs.find((l) => {
      const d = String(l.fecha || '').slice(0, 10);
      return d >= historyRange.from && d <= historyRange.to;
    });
    const next = inWeek
      ? String(inWeek.fecha).slice(0, 10)
      : (weekOffset === 0 ? todayYmd() : historyRange.to);
    if (next && next !== f) setHistoryFecha(next);
  }, [historyMode, historyRange.from, historyRange.to, historyLogs, weekOffset, historyFecha]);

  const openHistoryLog = async (log) => {
    if (!plan?.dias?.length || !log) return;
    await persistDiary({ silent: true });
    const idx = plan.dias.findIndex((d) => Number(d.dia) === Number(log.dia));
    if (idx >= 0) setSelectedDayIndex(idx);
    setHistoryFecha(String(log.fecha || '').slice(0, 10) || todayYmd());
    selectExercise(0);
    try {
      document.getElementById('training-athlete-day')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch { /* noop */ }
  };

  const goToToday = async () => {
    await persistDiary({ silent: true });
    setHistoryFecha(todayYmd());
    setWeekOffset(0);
    setHistoryMode('week');
  };

  const selectTrainingDay = async (idx) => {
    if (idx === selectedDayIndex) return;
    await persistDiary({ silent: true });
    setSelectedDayIndex(idx);
    selectExercise(0);
  };

  useEffect(() => {
    if (!sessionActive || !sessionStartedAt) return undefined;
    const tick = setInterval(() => {
      setSessionElapsedSec(Math.floor((Date.now() - sessionStartedAt) / 1000));
    }, 1000);
    return () => clearInterval(tick);
  }, [sessionActive, sessionStartedAt]);

  const sessionTimerLabel = useMemo(() => {
    const h = Math.floor(sessionElapsedSec / 3600);
    const m = Math.floor((sessionElapsedSec % 3600) / 60);
    const s = sessionElapsedSec % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }, [sessionElapsedSec]);

  const completedExercisesCount = useMemo(() => {
    if (!day?.ejercicios?.length) return 0;
    return day.ejercicios.filter((_, i) => {
      const log = workoutLog.exercises[i];
      return log?.reps
        || (log?.setReps && Object.values(log.setReps).some(Boolean))
        || (log?.setWeights && Object.values(log.setWeights).some(Boolean))
        || (log?.setIntensities && Object.values(log.setIntensities).some(Boolean));
    }).length;
  }, [day, workoutLog.exercises]);

  const currentExercise = useMemo(() => {
    const list = day?.ejercicios || [];
    return list[selectedExerciseIndex] || null;
  }, [day, selectedExerciseIndex]);

  useEffect(() => {
    const len = day?.ejercicios?.length || 0;
    if (len <= 0) {
      if (selectedExerciseIndex !== 0) setSelectedExerciseIndex(0);
      return;
    }
    if (selectedExerciseIndex >= len) setSelectedExerciseIndex(0);
  }, [day?.ejercicios?.length, selectedExerciseIndex]);

  const currentVideoUrl = useMemo(() => {
    if (!currentExercise) return '';
    if (currentExercise.youtube_url) return currentExercise.youtube_url;
    const key = String(currentExercise.exercise_name || '').trim().toLowerCase();
    return galleryByName[key]?.youtube_url || '';
  }, [currentExercise, galleryByName]);

  const currentVideoEmbed = useMemo(() => toEmbedUrl(currentVideoUrl), [currentVideoUrl]);

  const askAssistant = (exerciseName) => {
    setAssistantExercise(exerciseName);
    setAssistantReason('');
    setAssistantResult(null);
    setSubstituteAlternatives([]);
  };

  const applySubstitution = (substitution) => {
    if (!substitution?.exercise_name || !assistantExercise) return;
    setPlan((prev) => {
      if (!prev?.dias?.length) return prev;
      const next = { ...prev, dias: prev.dias.map((d) => ({ ...d, ejercicios: [...(d.ejercicios || [])] })) };
      const d = next.dias[selectedDayIndex];
      if (!d) return prev;
      const idx = (d.ejercicios || []).findIndex((e) => e.exercise_name === assistantExercise);
      if (idx === -1) return prev;
      const old = d.ejercicios[idx];
      d.ejercicios[idx] = {
        ...old,
        exercise_name: substitution.exercise_name,
        youtube_url: substitution.youtube_url || old.youtube_url || null,
        muscle_group: substitution.muscle_group || old.muscle_group,
        intensity_type: substitution.intensity_type || 'RPE/RIR',
        intensity_value: substitution.intensity_value ?? old.intensity_value ?? 7,
        prescription: {
          ...old.prescription,
          sets: substitution.sets || old.prescription?.sets || old.sets || 3,
          reps: substitution.reps || old.prescription?.reps || old.reps || '10-12',
          target_rpe: substitution.intensity_value ?? old.prescription?.target_rpe ?? 7,
        },
      };
      return next;
    });
    setAssistantResult(substitution);
  };

  const submitSubstitution = async () => {
    if (!assistantExercise) return;
    try {
      setAssistantLoading(true);
      const resp = await api.post('/training/ai-assistant/substitute', {
        exercise: assistantExercise,
        cause: assistantReason,
      });
      if (resp.data?.success) {
        const substitution = resp.data.substitution;
        setSubstituteAlternatives(resp.data.alternatives || []);
        applySubstitution(substitution);
      }
    } catch (e) {
      console.error(e);
      setAssistantResult({ error: e.response?.data?.error || 'No hay alternativa en la galería de tu entrenador.' });
      setSubstituteAlternatives([]);
    } finally {
      setAssistantLoading(false);
    }
  };

  const closeModal = () => {
    setAssistantExercise(null);
    setAssistantResult(null);
    setSubstituteAlternatives([]);
  };

  const toggleDayDone = (dayIdx) => {
    setPlan((prev) => {
      if (!prev?.dias?.length) return prev;
      const next = {
        ...prev,
        dias: prev.dias.map((d, i) =>
          i === dayIdx ? { ...d, completado: !d.completado } : d
        ),
      };
      void persistDiary({
        silent: true,
        completadoAck: true,
        override: { plan: next, day: next.dias[dayIdx] },
      });
      return next;
    });
  };

  const updateLogField = (exIndex, field, value, setIndex = null) => {
    setWorkoutLog((prev) => {
      const ex = prev.exercises[exIndex] || {};
      if (setIndex != null && (field === 'setReps' || field === 'setWeights' || field === 'setIntensities')) {
        const perSetField = field === 'setReps'
          ? 'setReps'
          : field === 'setWeights'
            ? 'setWeights'
            : 'setIntensities';
        const perSetValues = { ...(ex[perSetField] || {}), [setIndex]: value };
        return {
          ...prev,
          exercises: {
            ...prev.exercises,
            [exIndex]: { ...ex, [perSetField]: perSetValues },
          },
        };
      }
      return {
        ...prev,
        exercises: {
          ...prev.exercises,
          [exIndex]: { ...ex, [field]: value },
        },
      };
    });
  };

  const updateWellnessField = (field, value) => {
    setWorkoutLog((prev) => ({
      ...prev,
      wellness: {
        ...prev.wellness,
        [field]: value,
      },
    }));
  };

  const saveLog = async (fromSessionFinish = false) => {
    // onClick passes the event; only treat explicit `true` as finish.
    await persistDiary({
      silent: false,
      fromSessionFinish: fromSessionFinish === true,
      completadoAck: true,
    });
  };

  const startSession = () => {
    const now = Date.now();
    setSessionActive(true);
    setSessionStartedAt(now);
    setSessionElapsedSec(0);
    void persistDiary({
      silent: true,
      override: {
        sessionActive: true,
        sessionStartedAt: now,
        sessionElapsedSec: 0,
      },
    });
  };

  const finishSession = async () => {
    await persistDiary({ silent: false, fromSessionFinish: true });
    setSessionActive(false);
  };

  // Autosave silencioso del diario (notas / series / wellness) + draft local.
  useEffect(() => {
    if (!diaryHydratedRef.current || !plan?.id || !day?.dia) return undefined;
    writeDiaryDraft(plan.id, day.dia, historyFecha || todayYmd(), {
      workoutLog,
      sessionActive,
      sessionStartedAt,
      sessionElapsedSec,
    });
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => {
      void persistDiary({ silent: true });
    }, 900);
    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    };
  }, [
    workoutLog,
    plan?.id,
    day?.dia,
    day?.completado,
    historyFecha,
    persistDiary,
    writeDiaryDraft,
    sessionActive,
    sessionStartedAt,
  ]);

  // Flush al salir de pestaña / cerrar / desmontar (timer y diario no se pierden).
  useEffect(() => {
    const flush = () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
      void persistDiary({ silent: true });
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', flush);
      document.removeEventListener('visibilitychange', onVisibility);
      flush();
    };
  }, [persistDiary]);

  if (accessBlocked && !coachAssigned && subView !== 'measurements') {
    return (
      <div className="card training-coach-view training-light-island surface-readable max-w-3xl mx-auto p-6">
        <TrainingPendingBanner user={user} />
      </div>
    );
  }

  if (subView === 'measurements') {
    return (
      <div className="card training-coach-view training-light-island surface-readable max-w-7xl mx-auto">
        <TrainingFoodSyncBanner />
        <TrainingBodyMeasurements onBack={() => setSubView('workout')} />
      </div>
    );
  }

  if (subView === 'gallery') {
    const showBackToWorkout = initialSubView !== 'gallery';
    const coachOptions = [...new Set(galleryItems.map((i) => String(i.trainer_name || i.coach_name || '').trim()).filter(Boolean))].sort();
    const categoryOptions = [...new Set(galleryItems.map((i) => String(i.muscle_group || '').trim()).filter(Boolean))].sort();
    const filteredGallery = galleryItems.filter((item) => {
      if (galleryCoachFilter) {
        const coach = String(item.trainer_name || item.coach_name || '').trim();
        if (coach !== galleryCoachFilter) return false;
      }
      if (galleryCategoryFilter && String(item.muscle_group || '').trim() !== galleryCategoryFilter) return false;
      const q = gallerySearch.trim().toLowerCase();
      if (q) {
        const haystack = `${item.name || ''} ${item.muscle_group || ''} ${item.trainer_name || ''}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });

    return (
      <div className="card training-coach-view training-light-island surface-readable max-w-7xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-stone-900">Galería de videos</h2>
            <p className="text-sm text-stone-600 mt-1">
              {coachWl
                ? 'Biblioteca de contenido de tu entrenador — independiente del ciclo o rutina.'
                : 'Biblioteca de ejercicios — busca, filtra por categoría y reproduce videos.'}
            </p>
          </div>
          {showBackToWorkout ? (
            <button type="button" className="btn-secondary text-sm" onClick={() => setSubView('workout')}>
              Volver al entrenamiento
            </button>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-3 mb-4">
          <input
            type="search"
            className="input flex-1 min-w-[200px]"
            placeholder="Buscar ejercicio o categoría…"
            value={gallerySearch}
            onChange={(e) => setGallerySearch(e.target.value)}
          />
          {coachOptions.length > 1 ? (
            <select className="input max-w-xs" value={galleryCoachFilter} onChange={(e) => setGalleryCoachFilter(e.target.value)}>
              <option value="">Todos los entrenadores</option>
              {coachOptions.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          ) : null}
          {categoryOptions.length ? (
            <select className="input max-w-xs" value={galleryCategoryFilter} onChange={(e) => setGalleryCategoryFilter(e.target.value)}>
              <option value="">Todas las categorías</option>
              {categoryOptions.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
            </select>
          ) : null}
        </div>
        {filteredGallery.length ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredGallery.map((item) => (
              <button
                key={item.id || item.name}
                type="button"
                className="group border border-stone-200 rounded-xl overflow-hidden bg-white text-left hover:ring-2 hover:ring-lime-400/50 hover:shadow-md transition shadow-sm"
                onClick={() => setGalleryModalItem(item)}
              >
                <GalleryVideoThumbnail
                  url={item.youtube_url}
                  name={item.name}
                  muscleGroup={item.muscle_group}
                />
                <div className="p-3">
                  <p className="font-semibold text-stone-900 group-hover:text-lime-800 transition-colors">{item.name}</p>
                  {item.muscle_group ? (
                    <p className="text-xs text-stone-500 mt-1 uppercase tracking-wide">{item.muscle_group}</p>
                  ) : null}
                </div>
              </button>
            ))}
          </div>
        ) : (
          <p className="text-stone-600 text-sm">Tu entrenador aún no ha publicado videos en la galería.</p>
        )}
        {galleryModalItem ? (
          <div className="form-modal-overlay" onClick={() => setGalleryModalItem(null)}>
            <div
              className="form-modal ui-light-panel surface-readable training-gallery-video-modal w-full min-w-0 max-w-3xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="form-modal-content space-y-3 min-w-0">
                <h3 className="text-xl font-bold text-stone-900 break-words">{galleryModalItem.name}</h3>
                {toEmbedUrl(galleryModalItem.youtube_url) ? (
                  <div className="training-gallery-video-frame">
                    <iframe
                      title={galleryModalItem.name}
                      src={toEmbedUrl(galleryModalItem.youtube_url)}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                      allowFullScreen
                    />
                  </div>
                ) : (
                  <p className="text-sm text-stone-500">Sin video disponible.</p>
                )}
                <button type="button" className="btn-secondary" onClick={() => setGalleryModalItem(null)}>Cerrar</button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="card training-coach-view training-light-island surface-readable ui-light-surface max-w-7xl mx-auto relative min-h-[80vh]">
      {accessBlocked && coachAssigned ? (
        <div className="mb-6">
          <TrainingPendingBanner user={user} />
        </div>
      ) : null}

      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="text-3xl font-bold text-stone-900 mb-2">Tu entrenamiento</h2>
          <p className="text-stone-600 font-medium">
            {spokenCoach
              ? `${spokenCoach} te dejó la semana lista. Elige el día, lee las notas y registra lo que hagas.`
              : 'Elige el día, lee las notas de tu entrenador y registra lo que hagas. Es fácil.'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="btn-secondary text-sm"
            onClick={() => setSubView('gallery')}
          >
            Galería de videos
          </button>
          <button
            type="button"
            className="btn-secondary text-sm"
            onClick={() => setSubView('measurements')}
          >
            Medidas corporales
          </button>
        </div>
      </div>

      {!plan?.dias?.length && !coachAssigned && (
      <div className="bg-stone-50 border border-stone-200 p-5 rounded-2xl mb-8">
        <h3 className="text-lg font-bold text-stone-900 mb-4">Configuración</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div>
            <label className="label">Nivel</label>
            <select className="input" value={level} onChange={(e) => setLevel(e.target.value)}>
              <option value="principiante">Principiante</option>
              <option value="intermedio">Intermedio</option>
              <option value="avanzado">Avanzado</option>
            </select>
          </div>
          <div>
            <label className="label">Método</label>
            <select className="input" value={method} onChange={(e) => setMethod(e.target.value)}>
              <option value="hipertrofia">Hipertrofia</option>
              <option value="fuerza">Fuerza</option>
              <option value="resistencia">Resistencia</option>
            </select>
          </div>
          <div>
            <label className="label">Días / semana</label>
            <select className="input" value={daysAvailable} onChange={(e) => setDaysAvailable(Number(e.target.value))}>
              <option value={2}>2</option>
              <option value={3}>3</option>
              <option value={4}>4</option>
              <option value={5}>5</option>
              <option value={6}>6</option>
            </select>
          </div>
          <div className="flex items-end">
            <button className="btn-primary w-full" onClick={generateDailyPlan} disabled={loading}>
              {loading ? 'Generando…' : 'Plan generador'}
            </button>
          </div>
        </div>
      </div>
      )}

      {!plan?.dias?.length && coachAssigned && (
        <div className="bg-amber-50 border border-amber-200 p-5 rounded-2xl mb-8 text-sm text-stone-700">
          {spokenCoach ? `${spokenCoach} está armando tu rutina.` : 'Tu entrenador está armando tu rutina.'}
          {' '}Cuando la asigne la verás aquí. Si tardas, escríbele por WhatsApp desde Mi Cuenta.
        </div>
      )}

      {plan?.dias?.length > 0 && (coachAssigned || spokenCoach) ? (
        <AthleteCoachWelcome
          athleteName={spokenAthlete}
          coachName={spokenCoach}
          days={weekDays}
        />
      ) : plan?.dias?.length > 0 ? (
        <p className="text-sm text-lime-800 bg-lime-50 border border-lime-200 rounded-xl px-4 py-3 mb-6">
          Esta es tu rutina de la semana. Elige el día y registra lo que hagas: series, km o tiempo.
        </p>
      ) : null}

      {error && <div className="bg-red-50 text-red-600 p-4 rounded-xl mb-4 font-semibold">{error}</div>}

      {plan?.dias?.length > 0 && (
        <>
          <TrainingFoodSyncBanner />
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-stone-200 bg-white p-4 ui-light-surface training-session-bar">
            <div>
              <p className="text-sm font-semibold text-stone-900">
                Hoy toca — {formatDayWithWeekday(day || {}) || day?.nombre || 'tu sesión'}
                {dayIsSport ? (
                  <span className="ml-2 text-xs font-bold text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-full px-2 py-0.5">
                    {daySessionKind === SESSION_KINDS.RUNNING ? 'Running' : 'Natación'}
                  </span>
                ) : null}
              </p>
              <p className="text-xs text-stone-500">
                {dayIsSport
                  ? 'Lee las notas, escribe cuánto corriste o nadaste y guarda. Tres pasos, nada más.'
                  : `Progreso: ${completedExercisesCount}/${(day?.ejercicios || []).length} ejercicios · Tempo: ${sessionTimerLabel}`}
              </p>
              <p className="text-xs text-stone-600 mt-1 m-0">
                Así de fácil: <strong>1</strong> registra · <strong>2</strong> marca listo · <strong>3</strong> guarda lo que hiciste.
              </p>
            </div>
            {!dayIsSport ? (
            <div className="flex flex-wrap gap-2">
              {!sessionActive ? (
                <button type="button" className="training-action-btn text-sm inline-flex items-center gap-2" onClick={startSession}>
                  <span className="training-step-chip" aria-hidden="true">1/3</span>
                  Iniciar mi rutina
                </button>
              ) : (
                <button type="button" className="btn-secondary text-sm inline-flex items-center gap-2" onClick={finishSession} disabled={savingLog}>
                  <span className="training-step-chip training-step-chip--muted" aria-hidden="true">1/3</span>
                  Finalizar mi rutina
                </button>
              )}
            </div>
            ) : null}
          </div>
          <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-3 ui-light-surface">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-bold text-stone-800 m-0">Historial de entrenos</p>
              <p className="text-xs text-stone-600 m-0">{historyRange.label}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className={historyMode === 'week' ? 'btn-primary text-xs' : 'btn-secondary text-xs'}
                onClick={() => setHistoryMode('week')}
              >
                Por semana
              </button>
              <button
                type="button"
                className={historyMode === 'range' ? 'btn-primary text-xs' : 'btn-secondary text-xs'}
                onClick={() => {
                  setHistoryMode('range');
                  if (!rangeFrom && !rangeTo) {
                    const end = new Date();
                    const start = new Date();
                    start.setDate(start.getDate() - 29);
                    setRangeFrom(formatYmdLocal(start));
                    setRangeTo(formatYmdLocal(end));
                  }
                }}
              >
                Fechas específicas
              </button>
              <button
                type="button"
                className={historyMode === 'all' ? 'btn-primary text-xs' : 'btn-secondary text-xs'}
                onClick={() => setHistoryMode('all')}
              >
                Todo el historial
              </button>
              {isViewingPast && (
                <button type="button" className="btn-secondary text-xs" onClick={goToToday}>
                  Volver a hoy
                </button>
              )}
            </div>
            {historyMode === 'week' ? (
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  className="btn-secondary text-xs"
                  onClick={async () => {
                    await persistDiary({ silent: true });
                    setWeekOffset((o) => o - 1);
                  }}
                >
                  ← Semana anterior
                </button>
                <span className="text-sm font-semibold text-stone-800 min-w-[10rem] text-center">
                  {historyRange.label}
                </span>
                <button
                  type="button"
                  className="btn-secondary text-xs"
                  disabled={weekOffset >= 0}
                  onClick={async () => {
                    await persistDiary({ silent: true });
                    setWeekOffset((o) => Math.min(0, o + 1));
                  }}
                >
                  Semana siguiente →
                </button>
                {weekOffset !== 0 && (
                  <button type="button" className="btn-secondary text-xs" onClick={() => setWeekOffset(0)}>
                    Esta semana
                  </button>
                )}
              </div>
            ) : historyMode === 'range' ? (
              <div className="flex flex-wrap items-end gap-3">
                <label className="text-xs text-stone-600">
                  Desde
                  <input
                    type="date"
                    className="input mt-1 block text-sm"
                    value={rangeFrom}
                    max={rangeTo || undefined}
                    onChange={(e) => setRangeFrom(e.target.value)}
                  />
                </label>
                <label className="text-xs text-stone-600">
                  Hasta
                  <input
                    type="date"
                    className="input mt-1 block text-sm"
                    value={rangeTo}
                    min={rangeFrom || undefined}
                    onChange={(e) => setRangeTo(e.target.value)}
                  />
                </label>
                <button
                  type="button"
                  className="btn-secondary text-xs"
                  onClick={() => {
                    const end = new Date();
                    const start = new Date();
                    start.setDate(start.getDate() - 29);
                    setRangeFrom(formatYmdLocal(start));
                    setRangeTo(formatYmdLocal(end));
                  }}
                >
                  Últimos 30 días
                </button>
              </div>
            ) : null}
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {!historyLogs.length ? (
                <p className="text-xs text-stone-500 m-0">
                  No hay sesiones en {historyRange.label}. Prueba «Todo el historial» o «Últimos 30 días» para ver registros de otras fechas.
                </p>
              ) : (
                (historyMode === 'all' ? historyLogs.slice(0, 40) : historyLogs).map((log) => {
                  const active = String(log.fecha).slice(0, 10) === historyFecha
                    && Number(log.dia) === Number(day?.dia);
                  const status = trainingHistoryStatusLabel(log);
                  const histWd = weekdayLabel(
                    (plan?.dias || []).find((d) => Number(d.dia) === Number(log.dia))?.weekday,
                  );
                  return (
                    <button
                      key={log.id || `${log.fecha}-${log.dia}`}
                      type="button"
                      onClick={() => openHistoryLog(log)}
                      className={`w-full text-left rounded-lg border px-3 py-2 text-xs transition-colors ${
                        active
                          ? 'border-lime-500 bg-lime-50 text-stone-900'
                          : 'border-stone-200 bg-white text-stone-700 hover:bg-stone-50'
                      }`}
                    >
                      <span className="font-bold">{log.fecha}</span>
                      {' · '}{histWd ? `${histWd} · Día ${log.dia}` : `Día ${log.dia}`}
                      {log.history_ref_outside_week ? ' · última vez' : ''}
                      {status ? ` · ${status}` : ''}
                      {log.duration_minutes ? ` · ${log.duration_minutes} min` : ''}
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {isViewingPast ? (
            <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2 mb-4">
              Viendo historial del <strong>{historyFecha}</strong>. Puedes revisar o actualizar ese diario; usa «Volver a hoy» para el día actual.
            </p>
          ) : null}

          <div className="mb-5 flex flex-wrap gap-2">
            {plan.dias.map((d, idx) => (
              <button
                key={`day-${d.dia}`}
                onClick={() => {
                  void selectTrainingDay(idx);
                }}
                className={`px-4 py-2 rounded-full border text-sm font-semibold ${selectedDayIndex === idx
                  ? 'bg-lime-500 text-black border-lime-500'
                  : 'bg-white text-stone-700 border-stone-200'
                  }`}
              >
                {weekdayLabel(d.weekday)
                  ? `${weekdayLabel(d.weekday)}${d.nombre ? ` · ${String(d.nombre).replace(/^Día\s*\d+\s*[·:.\-–]\s*/i, '')}` : ''}`
                  : `Día ${d.dia}${d.nombre ? `: ${String(d.nombre).replace(/^Día\s*\d+\s*[·:.\-–]\s*/i, '')}` : ''}`}
              </button>
            ))}
          </div>

          <div id="training-athlete-day" className="mb-8 border border-stone-200 bg-white rounded-2xl p-5 training-athlete-day ui-light-surface">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-xl font-bold text-stone-900">
                {formatDayWithWeekday(day)}
              </h4>
              <div className="flex flex-col items-stretch sm:items-end gap-2">
                <p className="text-xs text-stone-600 m-0 text-right">
                  Cuando termines, marca listo y guarda
                </p>
                <div className="flex flex-wrap gap-3 justify-end">
                  <label className="flex items-center gap-2 text-sm text-stone-700 font-semibold cursor-pointer p-2 bg-stone-100 rounded-lg hover:bg-stone-200 transition-colors">
                    <span className="training-step-chip training-step-chip--muted" aria-hidden="true">2/3</span>
                    <input
                      type="checkbox"
                      className="w-5 h-5 rounded border-stone-300 text-lime-500 focus:ring-lime-500"
                      checked={Boolean(day?.completado)}
                      onChange={() => toggleDayDone(selectedDayIndex)}
                    />
                    Ya entrené
                  </label>
                  <button
                    onClick={() => { void persistDiary({ silent: false, completadoAck: true }); }}
                    disabled={savingLog}
                    className="training-action-btn text-sm inline-flex items-center gap-2"
                  >
                    <span className="training-step-chip" aria-hidden="true">3/3</span>
                    <span aria-hidden="true">📒</span>
                    {savingLog ? 'Guardando…' : 'Guardar lo que hice hoy'}
                  </button>
                </div>
              </div>
            </div>

            {dayIsSport ? (
              <div className="mb-6 space-y-3">
                {String(day?.coach_notes || '').trim() ? (
                  <p className="text-sm text-stone-800 m-0 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 break-words leading-snug">
                    <span className="font-bold text-amber-900">
                      {spokenCoach ? `${spokenCoach} te guía: ` : 'Tu entrenador te guía: '}
                    </span>
                    {String(day.coach_notes).trim()}
                  </p>
                ) : (
                  <p className="text-xs text-stone-600 m-0 rounded-xl border border-stone-200 bg-stone-50 px-3 py-2">
                    Si tu entrenador deja notas en este día, las verás aquí. Mientras tanto, sigue la distancia y el ritmo programados.
                  </p>
                )}
                <SportAthleteLogForm
                  sessionKind={daySessionKind}
                  prescription={day?.sport_prescription}
                  techniqueUrl={day?.technique_url || ''}
                  coachName={spokenCoach}
                  value={workoutLog.sportLog}
                  previousLog={previousLog}
                  onChange={(sportLog) => setWorkoutLog((prev) => ({ ...prev, sportLog }))}
                />
              </div>
            ) : null}

            {!dayIsSport ? (
            <>
            {String(day?.coach_notes || '').trim() ? (
              <p className="text-sm text-stone-800 mb-4 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 break-words leading-snug">
                <span className="font-bold text-amber-900">
                  {spokenCoach ? `${spokenCoach} te guía: ` : 'Tu entrenador te guía: '}
                </span>
                {String(day.coach_notes).trim()}
              </p>
            ) : null}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 relative">
              <div className="rounded-xl border border-lime-400 bg-lime-50 p-4 ui-light-surface">
                <h5 className="font-bold text-stone-900 text-sm mb-2 flex items-center gap-2">🔥 Calentamiento</h5>
                <p className="text-xs text-stone-600 mb-3">Rutina obligatoria antes de iniciar los ejercicios con carga para prevenir lesiones articulares.</p>
                {day?.warmup_url ? (
                  <a href={day.warmup_url} target="_blank" rel="noreferrer" className="block text-center text-sm font-semibold rounded-xl bg-black text-white py-2 hover:bg-stone-800 transition-all">Ver Video</a>
                ) : (
                  <p className="text-xs text-red-500">Video no asignado</p>
                )}
              </div>
              <div className="rounded-xl border border-stone-300 bg-stone-50 p-4 ui-light-surface">
                <h5 className="font-bold text-stone-900 text-sm mb-2 flex items-center gap-2">❤️ Cardio ({day?.cardio?.goal || 'oxidación'})</h5>
                <div className="grid grid-cols-2 gap-2 text-xs mb-2">
                  <div className="bg-white p-2 rounded-lg border border-stone-200 group">
                    <span className="block text-stone-500 mb-1">Pulsaciones (PPM)</span>
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        className="training-plan-field w-full text-base font-bold text-red-600 bg-white border border-stone-200 rounded px-2 py-1 outline-none focus:border-lime-400"
                        placeholder={day?.cardio?.bpm || 130}
                        value={workoutLog.cardioBpm}
                        onChange={e => setWorkoutLog(p => ({ ...p, cardioBpm: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="bg-white p-2 rounded-lg border border-stone-200">
                    <span className="block text-stone-500 mb-1">
                      {day?.cardio?.limit_type === 'distance' ? 'Distancia (km)' : 'Tiempo (min)'}
                    </span>
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        className="training-plan-field w-full text-base font-bold text-stone-900 bg-white border border-stone-200 rounded px-2 py-1 outline-none focus:border-lime-400"
                        placeholder={day?.cardio?.limit_value || 20}
                        value={workoutLog.cardioLimit}
                        onChange={e => setWorkoutLog(p => ({ ...p, cardioLimit: e.target.value }))}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="rounded-xl border border-sky-300 bg-sky-50 p-4 ui-light-surface">
                <h5 className="font-bold text-stone-900 text-sm mb-2 flex items-center gap-2">🧘 Estiramiento</h5>
                <p className="text-xs text-stone-600 mb-3">Estiramientos obligatorios post-entrenamiento e hipertrofia para elongación muscular.</p>
                {day?.stretching_url ? (
                  <a href={day.stretching_url} target="_blank" rel="noreferrer" className="block text-center text-sm font-semibold rounded-xl bg-black text-white py-2 hover:bg-stone-800 transition-all">Ver Video</a>
                ) : (
                  <p className="text-xs text-red-500">Video no asignado</p>
                )}
              </div>
              <div className="rounded-xl border border-violet-300 bg-violet-50 p-4 ui-light-surface">
                <h5 className="font-bold text-stone-900 text-sm mb-2 flex items-center gap-2">🎯 Técnica avanzada</h5>
                <p className="text-xs text-stone-600 mb-3">Video de técnica avanzada indicado por tu entrenador para este día.</p>
                {day?.technique_url ? (
                  <a href={day.technique_url} target="_blank" rel="noreferrer" className="block text-center text-sm font-semibold rounded-xl bg-black text-white py-2 hover:bg-stone-800 transition-all">Ver Video</a>
                ) : (
                  <p className="text-xs text-stone-400">Sin video asignado</p>
                )}
              </div>
            </div>

            {/* FACTORES DE BIENESTAR (WELLNESS) */}
            <div className="training-wellness-panel bg-slate-50 border border-stone-200 text-stone-900 p-5 rounded-2xl mb-6 ui-light-surface">
              <h5 className="font-bold text-lime-700 text-sm mb-4 uppercase tracking-widest flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>
                ¿Cómo te sentiste hoy?
              </h5>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-stone-600 uppercase tracking-tighter">Sueño (Horas)</label>
                  <input 
                    type="number" 
                    className="training-plan-field w-full bg-white border border-stone-200 text-stone-900 rounded-lg py-1 px-2 text-sm focus:ring-1 focus:ring-lime-500 focus:border-lime-400"
                    value={workoutLog.wellness.sleep_hours}
                    onChange={(e) => updateWellnessField('sleep_hours', Number(e.target.value))}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-stone-600 uppercase tracking-tighter">Calidad Sueño (1-10)</label>
                  <input 
                    type="range" min="1" max="10"
                    className="w-full accent-lime-500"
                    value={workoutLog.wellness.sleep_quality}
                    onChange={(e) => updateWellnessField('sleep_quality', Number(e.target.value))}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-stone-600 uppercase tracking-tighter">Estrés (1-10)</label>
                  <input 
                    type="range" min="1" max="10"
                    className="w-full accent-red-500"
                    value={workoutLog.wellness.stress_level}
                    onChange={(e) => updateWellnessField('stress_level', Number(e.target.value))}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-stone-600 uppercase tracking-tighter">Apetito (1-10)</label>
                  <input 
                    type="range" min="1" max="10"
                    className="w-full accent-blue-500"
                    value={workoutLog.wellness.appetite}
                    onChange={(e) => updateWellnessField('appetite', Number(e.target.value))}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-stone-600 uppercase tracking-tighter">Energía (1-10)</label>
                  <input 
                    type="range" min="1" max="10"
                    className="w-full accent-yellow-500"
                    value={workoutLog.wellness.energy_level}
                    onChange={(e) => updateWellnessField('energy_level', Number(e.target.value))}
                  />
                </div>
              </div>
            </div>

            <h4 className="text-lg font-bold text-stone-900 mb-3 mt-4">Tus ejercicios de hoy</h4>
            <p className="text-sm text-stone-600 mb-3">
              Ve en orden. Arriba ves lo que te programaron; abajo escribes lo que hiciste (reps, kilos). Toca un ejercicio para ver el video.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {(day?.ejercicios || []).map((ex, i) => (
                <div
                  key={`${ex.exercise_name}-${i}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => selectExercise(i)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      selectExercise(i);
                    }
                  }}
                  className={`rounded-xl border p-4 ui-light-surface cursor-pointer transition-colors text-left ${selectedExerciseIndex === i ? 'border-lime-500 bg-lime-50 ring-2 ring-lime-400' : 'border-stone-200 bg-white hover:border-stone-300'
                    }`}
                >
                  <p className="text-xs font-extrabold uppercase tracking-wide text-lime-800 mb-1">
                    Ejercicio {i + 1}
                    {selectedExerciseIndex === i ? ' · activo' : ''}
                  </p>
                  <div className="flex items-start justify-between gap-2">
                    <p
                      className="font-bold text-stone-900 text-sm uppercase tracking-tight"
                      title="Marcar como ejercicio activo"
                    >
                      {ex.exercise_name}
                    </p>
                    {trainer && (
                      <button
                        type="button"
                        className="text-xs px-2 py-1 rounded-lg border border-stone-300 bg-white text-stone-700 hover:bg-stone-100 whitespace-nowrap"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowTrainer(true);
                        }}
                        title="Ver datos del entrenador asignado"
                      >
                        Ver entrenador
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-stone-700 mt-2 font-medium break-words">
                    {exercisePrescriptionLabel(ex)}
                  </p>
                  {String(ex?.notes || '').trim() ? (
                    <p className="text-xs text-stone-800 mt-2 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5 break-words leading-snug">
                      <span className="font-bold text-amber-900">{spokenCoach ? `${spokenCoach}: ` : 'Tu entrenador: '}</span>
                      {String(ex.notes).trim()}
                    </p>
                  ) : null}
                  <div
                    className="mt-2"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    <label className="block text-[10px] font-bold uppercase tracking-wide text-sky-900 mb-1">
                      Cómo te fue
                    </label>
                    <textarea
                      className="w-full text-sm text-stone-800 rounded-lg border border-sky-200 bg-sky-50 px-2.5 py-2 leading-snug resize-y min-h-[3rem] outline-none focus:border-sky-400"
                      rows={2}
                      placeholder="Dolor, sensación, si cambiaste el peso…"
                      value={workoutLog.exercises[i]?.notes || ''}
                      onChange={(e) => updateLogField(i, 'notes', e.target.value)}
                    />
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {ex.youtube_url || galleryByName[String(ex.exercise_name || '').trim().toLowerCase()]?.youtube_url ? (
                      <button
                        type="button"
                        className="text-xs px-3 py-1 rounded-lg bg-stone-200 text-stone-800 font-semibold hover:bg-stone-300"
                        onClick={(e) => {
                          e.stopPropagation();
                          selectExercise(i);
                          try {
                            document.getElementById('training-exercise-video')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                          } catch { /* noop */ }
                        }}
                      >
                        Video de ejecución
                      </button>
                    ) : (
                      <span className="text-xs px-3 py-1 rounded-lg bg-stone-100 text-stone-500">Sin video</span>
                    )}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        askAssistant(ex.exercise_name);
                      }}
                      className="text-xs px-3 py-1 rounded-lg bg-lime-100 text-lime-900 font-semibold hover:bg-lime-200"
                    >
                      Sustituir (IA gratis)
                    </button>
                  </div>
                  <div
                    className="mt-4 p-3 bg-slate-50 border border-stone-200 rounded-lg"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    <p className="text-[10px] font-bold text-stone-600 mb-2 uppercase tracking-wide">Lo que hiciste tú</p>
                    <p className="text-xs text-stone-600 mb-3 leading-snug">
                      Escribe reps, kilos e intensidad de cada serie. Lo de arriba es la guía del entrenador.
                      {previousLog?.fecha ? (
                        <>
                          {' '}La franja ámbar es tu <strong>última sesión</strong> de este día ({String(previousLog.fecha).slice(0, 10)}), solo referencia.
                        </>
                      ) : null}
                    </p>
                    <div className="hidden sm:grid grid-cols-4 gap-2 mb-1 px-0.5">
                      <span className="text-[10px] text-stone-500 uppercase tracking-wide">Serie</span>
                      <span className="text-[10px] text-stone-500 uppercase tracking-wide">Reps</span>
                      <span className="text-[10px] text-stone-500 uppercase tracking-wide">Carga (kg)</span>
                      <span className="text-[10px] text-stone-500 uppercase tracking-wide">{ex.intensity_type || 'RPE/RIR'}</span>
                    </div>
                    {Array.from({ length: exerciseSetCount(ex) }, (_, si) => {
                      const prevHint = previousSetHint(previousLog, ex.exercise_name, i, si, { dia: day.dia });
                      return (
                      <div key={`set-${si}`} className="mb-2 last:mb-0">
                        {/* Móvil: tarjeta por serie (ordenada, inputs usables) */}
                        <div className="sm:hidden rounded-lg border border-stone-200 bg-white p-2.5 space-y-2">
                          <p className="text-xs font-bold text-stone-800">Serie {si + 1}</p>
                          <div className="grid grid-cols-3 gap-2">
                            <label className="block min-w-0">
                              <span className="block text-[10px] font-semibold uppercase tracking-wide text-stone-500 mb-1">Reps</span>
                              <input
                                type="text"
                                inputMode="numeric"
                                autoComplete="off"
                                className="training-plan-field w-full min-h-10 bg-white text-stone-900 border border-stone-200 rounded-lg px-2 py-2 text-base outline-none focus:border-lime-400"
                                value={workoutLog.exercises[i]?.setReps?.[si] || ''}
                                onChange={(e) => updateLogField(i, 'setReps', e.target.value, si)}
                              />
                            </label>
                            <label className="block min-w-0">
                              <span className="block text-[10px] font-semibold uppercase tracking-wide text-stone-500 mb-1">Carga</span>
                              <input
                                type="number"
                                step="0.5"
                                autoComplete="off"
                                className="training-plan-field w-full min-h-10 bg-white text-stone-900 border border-stone-200 rounded-lg px-2 py-2 text-base outline-none focus:border-lime-400"
                                value={workoutLog.exercises[i]?.setWeights?.[si] || ''}
                                onChange={(e) => updateLogField(i, 'setWeights', e.target.value, si)}
                              />
                            </label>
                            <label className="block min-w-0">
                              <span className="block text-[10px] font-semibold uppercase tracking-wide text-stone-500 mb-1">{ex.intensity_type || 'RIR'}</span>
                              <input
                                type="text"
                                inputMode="numeric"
                                autoComplete="off"
                                className="training-plan-field w-full min-h-10 bg-white text-stone-900 border border-stone-200 rounded-lg px-2 py-2 text-base outline-none focus:border-lime-400"
                                value={workoutLog.exercises[i]?.setIntensities?.[si] || ''}
                                onChange={(e) => updateLogField(i, 'setIntensities', e.target.value, si)}
                              />
                            </label>
                          </div>
                          <PrevWeekSetHint hint={prevHint} />
                        </div>
                        {/* Desktop / tablet: fila compacta */}
                        <div className="hidden sm:block">
                          <div className="grid grid-cols-4 gap-2 items-center">
                            <span className="text-xs font-semibold text-stone-700">S{si + 1}</span>
                            <input
                              type="text"
                              inputMode="numeric"
                              autoComplete="off"
                              className="training-plan-field w-full min-h-10 bg-white text-stone-900 border border-stone-200 rounded-lg px-2 py-2 text-base outline-none focus:border-lime-400"
                              value={workoutLog.exercises[i]?.setReps?.[si] || ''}
                              onChange={(e) => updateLogField(i, 'setReps', e.target.value, si)}
                            />
                            <input
                              type="number"
                              step="0.5"
                              autoComplete="off"
                              className="training-plan-field w-full min-h-10 bg-white text-stone-900 border border-stone-200 rounded-lg px-2 py-2 text-base outline-none focus:border-lime-400"
                              value={workoutLog.exercises[i]?.setWeights?.[si] || ''}
                              onChange={(e) => updateLogField(i, 'setWeights', e.target.value, si)}
                            />
                            <input
                              type="text"
                              inputMode="numeric"
                              autoComplete="off"
                              className="training-plan-field w-full min-h-10 bg-white text-stone-900 border border-stone-200 rounded-lg px-2 py-2 text-base outline-none focus:border-lime-400"
                              value={workoutLog.exercises[i]?.setIntensities?.[si] || ''}
                              onChange={(e) => updateLogField(i, 'setIntensities', e.target.value, si)}
                            />
                          </div>
                          <PrevWeekSetHint hint={prevHint} />
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            </>
            ) : null}
          </div>

          {!dayIsSport ? (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <div id="training-exercise-video" className="training-video-panel ui-light-surface rounded-xl border border-stone-200 bg-white p-5">
              <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                <div>
                  <h4 className="text-lg font-bold text-stone-900 mb-1">Video del ejercicio</h4>
                  <p className="text-sm text-stone-600 m-0">
                    {currentExercise ? (
                      <>
                        <span className="font-extrabold text-lime-800">Ejercicio {selectedExerciseIndex + 1}</span>
                        {' — '}
                        <strong className="text-stone-900">{currentExercise.exercise_name}</strong>
                      </>
                    ) : (
                      'Sin ejercicio'
                    )}
                  </p>
                </div>
                {(day?.ejercicios?.length || 0) > 1 ? (
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="btn-secondary text-xs"
                      disabled={selectedExerciseIndex <= 0}
                      onClick={() => selectExercise(selectedExerciseIndex - 1)}
                    >
                      ← Ejercicio anterior
                    </button>
                    <button
                      type="button"
                      className="btn-secondary text-xs"
                      disabled={selectedExerciseIndex >= (day.ejercicios.length - 1)}
                      onClick={() => selectExercise(selectedExerciseIndex + 1)}
                    >
                      Siguiente ejercicio →
                    </button>
                  </div>
                ) : null}
              </div>
              {currentVideoEmbed ? (
                <div
                  key={`video-${videoMountKey}-${selectedExerciseIndex}-${currentVideoEmbed}`}
                  className="relative w-full overflow-hidden rounded-xl border border-stone-200"
                  style={{ paddingTop: '56.25%' }}
                >
                  <iframe
                    key={`iframe-${videoMountKey}-${selectedExerciseIndex}`}
                    title={`Video ejercicio ${selectedExerciseIndex + 1}: ${currentExercise?.exercise_name || ''}`}
                    src={`${currentVideoEmbed}${currentVideoEmbed.includes('?') ? '&' : '?'}rel=0&modestbranding=1&vk=${videoMountKey}`}
                    className="absolute inset-0 w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-stone-300 p-8 text-stone-600 text-sm">
                  Video disponible próximamente. Mientras tanto, sigue las pautas de tu coach.
                </div>
              )}
            </div>

          </div>
          ) : null}
        </>
      )}

      {assistantExercise && (
        <div className="form-modal-overlay" onClick={closeModal}>
          <div className="form-modal ui-light-panel surface-readable w-full max-w-md relative" onClick={(e) => e.stopPropagation()}>
            <div className="form-modal-header">
            <h3 className="text-xl font-bold mb-0 text-stone-900">Sustituir ejercicio</h3>
            <button type="button" onClick={closeModal} className="form-close-btn" aria-label="Cerrar">×</button>
            </div>
            <div className="form-modal-content">
            <p className="text-xs text-lime-800 bg-lime-50 border border-lime-200 rounded-lg px-3 py-2 mb-3">
              Asistente gratuito: busca alternativas solo en la galería de tu entrenador (sin costo de API).
            </p>
            <p className="text-stone-600 text-sm mb-3">
              ¿Por qué quieres reemplazar <strong>{assistantExercise}</strong>?
            </p>
            <div className="flex flex-wrap gap-2 mb-3">
              {CAUSE_PRESETS.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  className={`text-xs px-2 py-1 rounded-full border ${assistantReason === preset ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-700 border-stone-300'}`}
                  onClick={() => setAssistantReason(preset)}
                >
                  {preset}
                </button>
              ))}
            </div>
            <textarea
              className="input mb-4 h-20"
              placeholder="Detalle opcional: dolor, equipo, fatiga…"
              value={assistantReason}
              onChange={(e) => setAssistantReason(e.target.value)}
            />
            {!assistantResult ? (
              <button className="btn-primary w-full" onClick={submitSubstitution} disabled={assistantLoading}>
                {assistantLoading ? 'Buscando en galería…' : 'Buscar sustitución'}
              </button>
            ) : (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 mt-2 space-y-3">
                {assistantResult.error ? (
                  <p className="text-red-600 text-sm">{assistantResult.error}</p>
                ) : (
                  <>
                    <h4 className="font-bold text-green-900">Sustitución aplicada</h4>
                    <p className="text-green-800 text-sm"><strong>{assistantResult.exercise_name}</strong></p>
                    <div className="flex flex-wrap gap-2">
                      <span className="text-xs bg-green-200 text-green-900 px-2 py-1 rounded font-bold">{assistantResult.sets}×{assistantResult.reps}</span>
                      {assistantResult.intensity_type && (
                        <span className="text-xs bg-green-200 text-green-900 px-2 py-1 rounded font-bold">
                          {assistantResult.intensity_type} {assistantResult.intensity_value}
                        </span>
                      )}
                    </div>
                    <p className="text-neutral-600 text-xs italic">{assistantResult.reason}</p>
                    {substituteAlternatives.length > 1 && (
                      <div>
                        <p className="text-xs font-bold text-stone-700 mb-2">Otras opciones de la galería:</p>
                        <div className="space-y-2 max-h-32 overflow-y-auto">
                          {substituteAlternatives.slice(1).map((alt) => (
                            <button
                              key={alt.exercise_name}
                              type="button"
                              className="w-full text-left text-xs px-3 py-2 rounded-lg border border-stone-200 bg-white hover:bg-stone-50"
                              onClick={() => applySubstitution({ ...assistantResult, ...alt, sets: assistantResult.sets, reps: assistantResult.reps })}
                            >
                              {alt.exercise_name}
                              {alt.muscle_group ? ` · ${alt.muscle_group}` : ''}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
            </div>
          </div>
        </div>
      )}

      {showTrainer && trainer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/40 backdrop-blur-sm p-4">
          <div className="training-modal-panel surface-readable bg-white rounded-3xl p-6 w-full max-w-md shadow-2xl relative">
            <button
              onClick={() => setShowTrainer(false)}
              className="absolute top-4 right-4 text-stone-400 hover:text-stone-800 font-bold text-xl"
              aria-label="Cerrar"
            >×</button>
            <h3 className="text-xl font-bold text-stone-900 mb-1">Tu entrenador</h3>
            <p className="text-stone-500 text-xs mb-4">Datos de contacto del coach asignado a tu plan.</p>
            <div className="space-y-2 text-sm">
              <p><span className="text-stone-500 text-xs uppercase tracking-wide block">Nombre</span><strong>{trainer.nombre || '—'}</strong></p>
              {trainer.especialidad && (
                <p><span className="text-stone-500 text-xs uppercase tracking-wide block">Especialidad</span>{trainer.especialidad}</p>
              )}
              {trainer.email && (
                <p>
                  <span className="text-stone-500 text-xs uppercase tracking-wide block">Email</span>
                  <a href={`mailto:${trainer.email}`} className="text-lime-700 hover:underline">{trainer.email}</a>
                </p>
              )}
              {trainer.telefono && (
                <p>
                  <span className="text-stone-500 text-xs uppercase tracking-wide block">Teléfono</span>
                  <a href={`tel:${trainer.telefono}`} className="text-lime-700 hover:underline">{trainer.telefono}</a>
                </p>
              )}
            </div>
            <button
              onClick={() => setShowTrainer(false)}
              className="btn-primary w-full mt-5"
            >Cerrar</button>
          </div>
        </div>
      )}
    </div>
  );
}
